/**
 * /for-employers — the QA Talent page. All of its copy, in one place.
 *
 * THE RULES THIS COPY IS HELD TO (confirmed by the owner, 2026-09-30; see
 * docs/superpowers/specs/2026-09-30-qa-talent-design.md):
 *
 * - Direct hire costs the employer nothing: no recruitment fee, placement fee,
 *   salary percentage or success fee, and no obligation to buy anything else.
 * - That is the only model that is free. WeAreQA providing or managing the
 *   engineers is a separately priced engagement, and is always said so.
 * - The page does not say why it is free.
 * - Screening claims are limited to what actually happens: an online QA test, a
 *   technical interview with an experienced QA engineer, an English check, and
 *   references. No background checks, no guarantee, no counts of anything.
 *
 * qaTalent.test.ts reads this file and fails on the banned words and claims.
 */

/** Options for the lead form's selects. The role list mirrors `roles` below, plus Other. */
export const leadFormOptions = {
  roles: [
    'Automation QA',
    'Manual QA',
    'SDET',
    'QA Lead / Manager',
    'Quality / Automation Architect',
    'API / Integration QA',
    'Performance QA',
    'Mobile QA',
    'AI / LLM QA',
    'Other',
  ],
  seniority: ['Middle', 'Senior', 'Lead', 'Flexible'],
  employmentType: ['Full-time', 'Part-time', 'Contract', 'Not sure yet'],
  startDate: ['ASAP', 'Within a month', 'In 1–3 months', 'Flexible'],
} as const;

export const seo = {
  title: 'Hire Automation QA & Manual QA Engineers | WeAreQA',
  description:
    'QA recruitment for employers hiring Automation QA and Manual QA engineers. QA-led screening, direct hire and zero recruitment or placement fees.',
} as const;

export const hero = {
  eyebrow: 'QA recruitment for employers',
  heading: 'Hire Automation QA & Manual QA Engineers.',
  lead: 'Build your QA team with engineers assessed by experienced QA engineers. We find and qualify Automation QA and Manual QA talent for your company. You interview and hire directly, with zero recruitment or placement fees.',
  primaryCta: 'Share Your Hiring Needs',
  secondaryCta: { label: 'Discuss QA hiring', href: '#book' },
  candidate: { lead: 'Looking for a QA job?', label: 'Go to the engineer page', href: '/for-qa-engineers' },
  trust: ['No recruitment fee', 'No placement fee', 'No salary percentage'],
  panel: {
    label: 'Recruitment cost to you',
    value: '$0',
    rows: [
      { label: 'Recruitment fee', value: '$0' },
      { label: 'Placement fee', value: '$0' },
      { label: 'Share of salary', value: '0%' },
    ],
    note: 'You employ the engineer directly.',
  },
} as const;

export const why = {
  heading: "Hiring QA shouldn't cost you a percentage of someone's salary.",
  body: [
    'Good QA engineers are hard to hire well. Recruitment is usually priced as a share of the hire’s salary, the screening is done on CVs and keywords, and the technical judgement is left to you.',
    'We do the part that takes QA knowledge: finding people who can do the work, and testing them on it. You don’t pay us for it.',
  ],
} as const;

export type TalentRole = { title: string; skills: string[] };

/** Reusable: the future role pages read the same list. */
export const roles: TalentRole[] = [
  { title: 'Automation QA', skills: ['Playwright, Selenium, Cypress', 'Java, TypeScript / JavaScript, Python, C#', 'API automation', 'CI/CD'] },
  { title: 'Manual QA', skills: ['Web and mobile testing', 'API testing', 'Requirements analysis', 'Exploratory and regression testing'] },
  { title: 'SDET', skills: ['Test frameworks built from scratch', 'Unit, contract and integration tests', 'CI pipelines and test infrastructure'] },
  { title: 'QA Lead / Manager', skills: ['Test strategy and planning', 'Leading and growing a QA team', 'Release quality and reporting'] },
  { title: 'Quality / Automation Architect', skills: ['Framework and test architecture', 'Tooling decisions', 'Scaling automation across teams'] },
  { title: 'API / Integration QA', skills: ['REST, GraphQL and gRPC', 'Contract testing', 'Postman, REST Assured'] },
  { title: 'Performance QA', skills: ['JMeter, k6, Gatling', 'Load and stress testing', 'Bottleneck analysis'] },
  { title: 'Mobile QA', skills: ['iOS and Android', 'Appium, Espresso, XCUITest', 'Device and OS coverage'] },
  { title: 'AI / LLM QA', skills: ['LLM and chatbot evaluation', 'Adversarial and safety testing', 'AI-assisted test generation'] },
];

export const rolesSection = {
  eyebrow: 'Roles',
  heading: 'Automation QA and Manual QA for your team',
  lead: 'Hire Automation QA for your test stack or Manual QA for web, mobile and API testing. We also recruit SDET, QA leadership and specialist QA roles. Tell us which skills your team needs.',
} as const;

export const vetting = {
  eyebrow: 'Assessment',
  heading: 'QA people evaluated by QA people.',
  lead: 'A general recruiter reads CVs. Our candidates are assessed by people who do QA for a living.',
  steps: [
    { title: 'Online QA test', body: 'A practical test for the role and stack: manual, or automation in Java, JS/TS, Python or C#.' },
    { title: 'Technical interview', body: 'With an experienced QA engineer, on real testing problems rather than definitions.' },
    { title: 'English check', body: 'Spoken and written, for working in an international team.' },
    { title: 'References', body: 'From people they have worked with.' },
  ],
  criteriaHeading: 'What we look at',
  criteria: [
    'Automation stack and frameworks',
    'Testing fundamentals',
    'API knowledge',
    'Software engineering skills',
    'Communication',
    'English level',
    'Seniority fit',
    'Availability',
    'Time-zone fit',
    'Domain experience',
  ],
} as const;

export const steps = {
  eyebrow: 'How it works',
  heading: 'Four steps, and you only pay the engineer',
  items: [
    { number: '01', title: 'Tell us who you need', body: 'Role, seniority, technologies, location or time zone, and when you want them to start.' },
    { number: '02', title: 'We search and qualify', body: 'We find QA engineers who match the technical need and put them through our assessment.' },
    { number: '03', title: 'You interview', body: 'You meet the candidates you want to evaluate, and only those.' },
    { number: '04', title: 'You hire directly', body: 'You decide, and the engineer joins your company on your terms.' },
  ],
  conclusion: { label: 'Recruitment cost to you', value: '$0' },
} as const;

export const fees = {
  eyebrow: 'Pricing',
  heading: 'No recruitment fee. Really.',
  lead: 'This is how the service works, not a promotion. When you hire directly, there is:',
  dontPay: [
    'No upfront charge',
    'No placement fee',
    'No success fee',
    'No percentage of salary',
    'No obligation to buy anything else from us',
  ],
  managedNote:
    'Rather we provide and manage the engineers for you? That’s a Quality Operations engagement, priced separately, and only if you ask for it.',
  managedCta: { label: 'See Quality Operations', href: '/services/intelligent-quality-operations' },
} as const;

export const comparison = {
  eyebrow: 'Compare',
  heading: 'A different way to hire QA',
  columns: ['Traditional recruitment', 'WeAreQA QA Talent'],
  rows: [
    { label: 'Recruitment fee', traditional: 'Usually a share of salary', weareqa: '$0' },
    { label: 'QA specialisation', traditional: 'Often generalist', weareqa: 'QA only' },
    { label: 'Technical qualification', traditional: 'CV and keyword screening', weareqa: 'QA test and interview by a QA engineer' },
    { label: 'Direct hire', traditional: 'Yes', weareqa: 'Yes, the engineer joins your company' },
    { label: 'Obligation to buy other services', traditional: 'Varies', weareqa: 'None' },
  ],
} as const;

export const whyUs = {
  eyebrow: 'Why WeAreQA',
  heading: 'We know what a strong QA engineer looks like',
  body: 'Because we build and run quality engineering ourselves: test architecture, automation, API and integration testing, AI validation and managed quality operations, for teams shipping regulated and software-heavy products.',
  servicesLabel: 'What we do day to day',
  proof: {
    rating: '5.0',
    label: 'on Clutch',
    detail: '4 verified reviews',
    href: 'https://clutch.co/profile/weareqa',
    linkLabel: 'Read the reviews',
  },
} as const;

export const industries = {
  heading: 'Where our QA experience comes from',
  items: [
    { label: 'Financial Services & FinTech', href: '/industries/fintech' },
    { label: 'Healthcare & HealthTech', href: '/industries/healthcare' },
    { label: 'Pharma', href: '/industries/pharma' },
    { label: 'eCommerce & SaaS', href: '/industries/ecommerce' },
    { label: 'Communications', href: '/industries/communications' },
  ],
} as const;

export const formSection = {
  eyebrow: 'For employers',
  heading: 'Tell us who your company needs to hire.',
  lead: 'Share your company, work email, name, role and hiring needs, then confirm the terms. We follow up by email to clarify the brief.',
} as const;

export const faq: { q: string; a: string }[] = [
  {
    q: 'How is WeAreQA different from a QA recruitment agency?',
    a: 'We specialise in QA recruitment for employers. Candidates take an online QA test, a technical interview with a QA engineer, an English check and reference checks. You interview and hire directly, with no recruitment or placement fee.',
  },
  {
    q: 'Is hiring through WeAreQA really free for employers?',
    a: 'Yes, when you hire the engineer directly. You pay us no recruitment fee, no placement fee and no percentage of salary, not upfront and not at hire. You pay the engineer, as their employer.',
  },
  {
    q: 'Do you charge a placement fee or a percentage of salary?',
    a: 'No. Neither, and there is no success fee either.',
  },
  {
    q: 'Which QA roles can you help us hire?',
    a: 'Automation QA, Manual QA, SDET, QA Lead or Manager, Quality or Automation Architect, API and Integration QA, Performance QA, Mobile QA and AI or LLM QA. If your role sits between these, tell us.',
  },
  {
    q: 'Can you find Automation QA engineers?',
    a: 'Yes. We look at the stack you actually use, such as Playwright, Cypress or Selenium with Java, TypeScript, Python or C#, and test candidates on it rather than on keywords.',
  },
  {
    q: 'Can you find Manual QA engineers?',
    a: 'Yes: web, mobile and API testing, exploratory and regression work, and requirements analysis. Candidates take a practical QA test as part of the assessment.',
  },
  {
    q: 'Can we hire candidates directly?',
    a: 'Yes, that is the model. The engineer joins your company on your contract, and we are not in the middle.',
  },
  {
    q: 'Do we need to buy other WeAreQA services?',
    a: 'No, there is no obligation. If you later want us to provide or run a QA team for you, that is a separate, paid engagement, and only if you ask for it.',
  },
  {
    q: 'Which countries and time zones do you cover?',
    a: 'We search worldwide, for remote roles. Tell us the location or the time-zone overlap you need, and we search for that.',
  },
  {
    q: 'How are candidates evaluated?',
    a: 'Through an online QA test, a technical interview with an experienced QA engineer, an English check and references.',
  },
  {
    q: 'What happens if none of the candidates fit?',
    a: 'Tell us why, and we search again with that in mind. If you decide to stop, you stop: there is nothing to pay and nothing to cancel.',
  },
  {
    q: 'Can you help us hire several QA engineers?',
    a: 'Yes. Put the number in the form, and we plan the search around the roles and the order you need them in.',
  },
];

export const faqSection = {
  eyebrow: 'FAQ',
  heading: 'Questions employers ask',
} as const;

export const finalCta = {
  heading: 'Ready to hire Automation QA or Manual QA?',
  body: 'Tell us about your team, testing stack and the role you need to fill. Hire directly with no recruitment or placement fee.',
  cta: 'Share Your Hiring Needs',
  candidate: { lead: 'Looking for a QA role instead?', label: 'See how we work with engineers', href: '/for-qa-engineers' },
} as const;

/** Structured data for the page: a Service, free to the employer. No reviews, ratings or JobPosting. */
export function talentServiceJsonLd(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'QA Talent: direct hiring of QA engineers',
    serviceType: 'QA engineer recruitment',
    provider: { '@id': 'https://weareqa.com/#organization' },
    areaServed: 'Worldwide',
    url: 'https://weareqa.com/for-employers',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'No recruitment or placement fee for employers hiring directly.',
    },
  };
}
